import PageLayout from "@/components/PageLayout";
import { useSEO } from "@/hooks/useSEO";

const Privacy = () => {
  useSEO({
    title: "Privacy Policy - Ommni",
    description: "Read Ommni's Privacy Notice to understand how we collect, use, and protect your personal information when using our commerce operations platform.",
    keywords: "privacy policy, data protection, personal information, GDPR, privacy notice"
  });

  return (
    <PageLayout>
      <div className="container mx-auto px-4 py-16 max-w-4xl">
        <article className="prose prose-slate dark:prose-invert max-w-none">
          <h1>Ommni Privacy Notice</h1>
          
          <p className="text-sm text-muted-foreground">Last Updated: January 1, 2025</p>

          <p>
            At Ommni Inc. (the "Company" or "we"), we respect your privacy and are committed to protecting it through our compliance with this privacy notice ("Notice").
          </p>

          <p>
            This Notice describes the types of information we may collect from you or that you may provide when you use our software platform and proprietary technology, including all versions of Ommni website at ommni.io, the Ommni mobile applications, the Ommni software, content therein and related technologies (the "Services"). It also describes our practices for collecting, using, maintaining, protecting, and disclosing that information as well as your associated rights. Terms used and not defined in this Notice have the respective meanings assigned to them in the <a href="/terms">Terms of Use</a>.
          </p>

          <p>
            Please read this Notice carefully to understand our policies and practices regarding your information and how we will treat it. If you do not agree with our policies and practices, your choice is not to use our Service. By accessing or using this Service, you agree to this Notice. This Notice may change from time to time. Your continued use of this Service after we make changes is deemed to be acceptance of those changes, so please check the Notice periodically for updates.
          </p>

          <h2>1. Children Under the Age of 18</h2>

          <p>
            Our Service is not intended for children under 18 years of age. No one under the age 18 may provide any personal information to or on the Service. We do not knowingly collect personal information from children under 18. If you are under 18, do not use or provide any information on this Service or on or through any of its features or to register on the Service, make any purchases through the Service, use any of the interactive or public comment features of this Service or provide any information about yourself to us, including your name, address, telephone number, e-mail address or any screen name or user name you may use. If we learn we have collected or received personal information from a child under 18 without verification of parental consent, we will delete that information. If you believe we might have any information from or about a child under 18, please contact us via our contact us link.
          </p>

          <h2>2. Collection of Personal Information</h2>

          <p>
            For the purpose of this Notice, "personal information" means all information that identifies, relates to, describes, can be associated with, or could reasonably identify you as an individual. The provision of certain types of personal information may be mandatory or optional. Your personal information can be provided directly by you or collected automatically.
          </p>

          <p>
            We receive and store, and may use third-party vendors to receive and store, any information you knowingly provide to us when you create an account, make a purchase, contact us with a message, post, upload or otherwise submit to our Services a comment, photo, or other content or user-generated material, register for one of our events, participate in a survey, or fill out any form on the Service.
          </p>

          <h2>3. Personal Information You Provide Directly to Us</h2>

          <p>
            The personal information that we collect depends on the context of your interactions with us and the Services, the choices you make, and the products and features you use. The personal information we collect may include the following:
          </p>

          <ul>
            <li>Name(s)</li>
            <li>Email address</li>
            <li>Phone number</li>
            <li>Company name</li>
            <li>Billing address</li>
          </ul>

          <p>
            Besides, you might voluntarily upload any content that includes personally identifiable information contained in input, content uploads, or feedback that you provide to our Services.
          </p>

          <p>
            All personal information that you provide to us must be true, complete, and accurate, and please notify us of any changes to such personal information.
          </p>

          <h3>Personal information We Automatically Collect from You</h3>

          <p>
            We also automatically collect certain technical data that is sent to us from the computer, mobile device and/or browser through which you access the Service.
          </p>

          <h4>Device Data</h4>

          <p>
            When you visit or use the Service, we may collect identifiers and internet activity information such as IP address, device information, browser type, plug-ins, integrations, internet service provider, mobile carrier, the pages and files viewed, referring website, operating system, system configuration information, and date and time stamps associated with your usage.
          </p>

          <h4>Usage Data</h4>

          <p>
            We collect information about your usage and activity on the Service, which may include the content of the Service that you visit, the time and date of your visit, the time spent on those pages, views and interactions with content, frequency of your interactions on the Service, and other diagnostic data.
          </p>

          <h4>Location Data</h4>

          <p>
            We collect information about your approximate location, including location information based on your IP address.
          </p>

          <h4>Cookies, Web Beacons, and Other Tracking Technologies</h4>

          <p>
            We use cookies and other tracking technologies to provide certain functionality and features, to recognize you across different services and devices, and to enhance your experience by measuring and analyzing how you use the Service. The technologies we use may include:
          </p>

          <ul>
            <li><strong>Cookies:</strong> Cookies are small bits of data stored by a website on your computer or mobile device's hard drive to remember details about your visit. We employ session cookies, which expire when you close your web browser, to help us gather additional information and improve your experience with the Service. If you prefer not to have cookies stored on your hard drive, you may have the option to disable this feature on your computer or mobile device. Refer to your Internet browser's documentation for guidance on how to do this and how to delete persistent cookies. However, if you choose to reject cookies from us, our Services may not work as intended.</li>
            <li><strong>Web Beacons:</strong> The Service may contain web beacons, small graphic images, or other web programming code that permit us, for example, to count users who have visited our Service and for other related website statistics.</li>
          </ul>

          <h2>4. Use of Collected Information</h2>

          <p>
            We process your personal information only when we have a lawful basis. Presently, we have a legitimate interest and, in some cases, your consent as the lawful basis for processing. Our legitimate interest is to deliver the Service to you. We have determined that our processing of your personal information is necessary to deliver the Service to you, and that there is no less intrusive means of delivering you the Service.
          </p>

          <p>
            In some cases, we may use consent as our lawful basis for collecting and processing certain types of personal information. In such cases, you have a right to withdraw the consent at any time.
          </p>

          <p>
            In some cases, we may also have a legal obligation to collect personal information from you or may otherwise need the personal information to protect your vital interests or those of another person.
          </p>

          <p>Any of the information that we collect from you may be used for the following purposes:</p>

          <ul>
            <li>To register and manage user account;</li>
            <li>To present our Service and its contents to you;</li>
            <li>To provide you with information or Service that you request from us;</li>
            <li>To improve our Services and provide customer support;</li>
            <li>To provide you with notices about your account/subscription, including expiration and renewal notices;</li>
            <li>To carry out our obligations and enforce our rights arising from any contracts entered into between you and us, including for billing and collection;</li>
            <li>To notify you about changes to our Service or any products or services we offer or provide through it;</li>
            <li>To send you marketing communications;</li>
            <li>To allow you to participate in interactive features on our Service;</li>
            <li>To maintain safety and security of our Services;</li>
            <li>To fulfill any purpose for which you provide it;</li>
            <li>To comply with legal obligations; and</li>
            <li>For any other purpose with your consent.</li>
          </ul>

          <p>
            We may use the information we have collected from you on an anonymized, aggregated basis to enable us to display advertisements to our advertisers' target audiences. Even though we do not disclose your personal information to advertisers for these purposes without your consent, if you click on or otherwise interact with an advertisement, the advertiser may assume that you meet its target criteria.
          </p>

          <h2>5. Disclosure of Information</h2>

          <p>
            We may disclose aggregated information about our users, and information that does not identify any individual, without restriction.
          </p>

          <p>
            Depending on the requested Service or as necessary to complete any transaction or provide any Service you have requested, we may share your information with our contracted companies and services providers (collectively, "Service Providers") we rely upon to assist in the operation of the Service available to you who are bound by contractual obligations to keep your personal information confidential and use it only for the purposes for which we disclose it to them. We will not share any personally identifiable information with unaffiliated third parties.
          </p>

          <p>
            Service Providers are not authorized to use or disclose your information except as necessary to perform services on our behalf or comply with legal requirements. Service Providers are given the information they need to perform their designated functions, and we do not authorize them to use or disclose any of the provided information for their own marketing or other purposes.
          </p>

          <p>
            We may also disclose personal information we collect, use or receive if required or permitted by law, such as to comply with a subpoena or similar legal process, and when we believe in good faith that disclosure is necessary to protect our rights, protect your safety or the safety of others, enforce contractual obligations, investigate fraud or respond to a government request.
          </p>

          <p>
            In the event we go through a business transition, such as a merger or acquisition by another company, sale of all or portion of its assets, dissolution, or as part of a bankruptcy, liquidation or similar proceeding, your personal information will likely be among the assets transferred.
          </p>

          <p>
            Finally, we may disclose your personal information for any reason with your consent or for any purpose disclosed by us when you provide the information.
          </p>

          <h2>6. Payment Processing</h2>

          <p>
            In the case of Services requiring payment, you may be required to provide your credit card details or other payment account information, which will be used solely for processing payments. We use third-party payment processors ("Payment Processors") to assist us in processing your payment information securely.
          </p>

          <p>
            Our Payment Processors adhere to the latest security standards as managed by the PCI Security Standards Council, which is a joint effort of brands like Visa, MasterCard, American Express and Discover. We will share payment data with the Payment Processors only to the extent necessary for the purposes of processing your payments, refunding such payments, and dealing with complaints and queries relating to such payments and refunds.
          </p>

          <p>
            Please note that the Payment Processors may collect some personal information from you, which allows them to process your payments (e.g. your email address, address, credit card details, and bank account number) and handle all the steps in the payment process through their systems, including data collection and data processing. Where necessary for future or recurring payments and subject to your prior consent, your financial information will be stored in encrypted form on secure servers of our Payment Processors. The Payment Processors' use of your personal information is governed by their respective privacy policies which may or may not contain privacy protections as protective as this Notice. We suggest that you review their respective privacy policies.
          </p>

          <h2>7. Identity Verification</h2>

          <p>
            We may also ask you to permit us to collect data about you from third-party providers to verify your identity, do a credit check and prevent fraud or assess risk. This helps us ensure that you are using our Services legally, that you are eligible for the Service you want to use, and to protect your data and our Services from fraudsters who may put you and your money at risk.
          </p>

          <h2>8. Accessing and Correcting Your Information</h2>

          <p>
            You may send us an e-mail via our contact link to request access to, correct or delete any personal information that you have provided to us. We cannot delete your personal information except by also deleting your user account, which may result in a diminishment to the Service we are able to provide to you. However, we will honor verified deletion requests, provided however that we will not accommodate a request to change information if we believe the change would violate any law or legal requirement or cause the information to be incorrect.
          </p>

          <p>
            If you delete Your Content from the Service, copies of Your Content may remain viewable in cached and archived pages or might have been copied or stored by other Service users. Access and use of information provided on the Service, including Your Content, is governed by our Terms of Use.
          </p>

          <h2>9. Retention of Information</h2>

          <p>
            Unless you ask us to delete your account and associated personal information sooner, we will retain and use your personal information for the period necessary to comply with our legal obligations, until the purchase order or Service is fulfilled, to enforce our Notice, resolve disputes, and unless a longer retention period is required or permitted by law.
          </p>

          <p>
            We may use any aggregated data from incorporating your personal information after you update or delete it, but not in a manner that would identify you personally. Once the retention period expires, personal information shall be deleted. Therefore, the right to access, the right to erasure, the right to ratification, and the right to data portability cannot be enforced after the expiration of the retention period.
          </p>

          <h2>10. Data Security</h2>

          <p>
            We have implemented measures designed to secure your personal information from accidental loss and from unauthorized access, use, alteration, and disclosure.
          </p>

          <p>
            The safety and security of your information also depends on you. Where we have given you (or where you have chosen) a password for access to certain parts of our Service, you are responsible for keeping this password confidential. We ask you not to share your password with anyone.
          </p>

          <p>
            Unfortunately, the transmission of information via the internet is not completely secure. Although we do our best to protect your personal information, we cannot guarantee the security of your personal information transmitted to our Service. Any transmission of personal information is at your own risk. We are not responsible for circumvention of any privacy setting or security measures contained in the Service.
          </p>

          <h2>11. Your Rights Regarding Your Personal information</h2>

          <p>
            Depending on the applicable privacy and data protection laws that apply to your situation, you may be able to exercise the following rights regarding your personal information processing by us:
          </p>

          <h3>Right to access</h3>

          <p>
            You have the right to require: the confirmation on whether we process your personal information or not; a copy of such personal information; and information on the use of your personal information in a clear, transparent, and understandable way (as provided in this Notice).
          </p>

          <h3>Right to rectification</h3>

          <p>
            You have the right to require the rectification of your personal information that may be obsolete, inaccurate or incomplete.
          </p>

          <h3>Right to erasure</h3>

          <p>You have the right to require the erasure of your personal information, when one of the following conditions applies:</p>

          <ul>
            <li>Your personal information is no longer necessary for the purposes for which we collected it or otherwise processed;</li>
            <li>You retract your consent on which the processing of your personal information is based, and we have no other legal basis for the processing;</li>
            <li>You object to the personal information processing and we have no compelling legitimate grounds for processing your personal information;</li>
            <li>The use that is made of your personal information does not comply with the applicable legal or regulatory provisions.</li>
          </ul>

          <h3>Right to restriction of processing</h3>

          <p>You have the right to require the restriction of the personal information processing during a limited period of time, in particular in order to carry out some verifications, if one of the following conditions applies:</p>

          <ul>
            <li>You contest the accuracy of your personal information, and wish to restrict the processing of your personal information during the period required of us to verify the accuracy of your personal information;</li>
            <li>The personal information processing is unlawful and you oppose the erasure of your personal information and request the restriction of their use instead;</li>
            <li>We no longer need your personal information for the purposes of the processing, but you require them for the establishment, exercise, or defense of legal claims;</li>
            <li>You have objected to the processing of your personal information, and wish to restrict their processing pending the verification of whether our compelling legitimate grounds for processing override your grounds for objecting to it.</li>
          </ul>

          <h3>Right to data portability</h3>

          <p>You have the right to require us to provide you with your personal information that you have provided to us, in a structured, commonly used and machine-readable format. You also have the right to require us to transmit this data to another data controller. This right is not exercised in all circumstances, it applies only if it fulfils all the following conditions:</p>

          <ul>
            <li>Your request is only related to your personal information (excluding anonymous or third-party data);</li>
            <li>Your request does not adversely affect the rights or freedoms of others, in particular those of us (including trade secrets);</li>
            <li>The processing is carried out by automated means (paper files are therefore not included);</li>
            <li>The processing is based on consent or the performance of a contract.</li>
          </ul>

          <h3>Right to object</h3>

          <p>You have the right to object to our processing of your personal information at any time if:</p>

          <ul>
            <li>the processing is based on our legitimate interest; or</li>
            <li>your personal information are processed for direct marketing purposes.</li>
          </ul>

          <p>
            We will no longer process your personal information unless we demonstrate compelling legitimate grounds for the processing which override your interests, rights and freedoms, such as the respect of a legal obligation (e.g. legal obligation involving the retention of personal information), or for the establishment, exercise or defense of legal claims.
          </p>

          <h3>Right to withdraw your consent</h3>

          <p>
            You have the right to withdraw your consent when it has been obtained, without this withdrawal affecting the lawfulness of the personal information processing operations previously carried out.
          </p>

          <p>
            You have control regarding our use of personal information for directing marketing. If you no longer wish to receive any marketing communications, remain on a mailing list to which you previously subscribed, or receive any other marketing communication, you can choose to not receive such communications at any time. Please follow the unsubscribe link in the relevant communication or contact us.
          </p>

          <p>
            Depending on your country of residence, you may have additional local rights with respect to our processing of personal information. Please note that some of the personal information that we collect, use and share may be exempt from the rights outlined above.
          </p>

          <p>
            To exercise your rights, please contact us by using the information below. Your personal information may be processed in accordance with these rights. We try to respond to all legitimate requests within one month unless otherwise required by law, and will contact you if we need additional information from you in order to honor your request or verify your identity. Occasionally it may take us longer than a month, taking into account the complexity and number of requests we receive.
          </p>

          <h2>12. Changes to Our Privacy Notice</h2>

          <p>
            It is our policy to post any changes we make to our Notice on this page. If we make material changes to how we treat our users' personal information, we will notify you by e-mail to the e-mail address specified in your account and/or through a notice on the Service home page. The date the Notice was last revised is identified at the top of the page. You are responsible for ensuring we have an up-to-date, active and deliverable e-mail address for you, and for periodically visiting our Service and this Notice to check for any changes.
          </p>

          <h2>13. Contact Information</h2>

          <p>
            To ask questions or comment about this Notice or our privacy practices, contact us at <a href="mailto:info@ommni.io">info@ommni.io</a>.
          </p>

          <p>Thank you for visiting our Service.</p>
        </article>
      </div>
    </PageLayout>
  );
};

export default Privacy;
